import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as CatalogsVehicleActions from './catalog-vehicle.actions';
import { CatalogsVehicleStoreService } from './catalog-vehicle.service';
import { ArkikAlertService } from 'src/app/shared/components/arkik-alert/arkik-alert.service';
import { TranslationService } from 'src/app/core/services/common/translation.service';
import { map, switchMap, catchError, mergeMap, take, timeout, takeUntil } from 'rxjs/operators';
import { Observable, defer, from, of, merge } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectCatalogsVehicleByUserGuid } from './catalog-vehicle.selectors';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { CatalogsVehicleInterface } from './catalog-vehicle.models';
import { CATALOG_OPERATION_TIMEOUT_MS, reportCatalogError } from '@core/services/common/catalog-operation';
import { loadLogin, logout, resetLogin } from '../login/login.actions';

const hasCompleteVehicleCatalogsData = (c: CatalogsVehicleInterface | null | undefined): boolean =>
  !!c &&
  [c.EquipmentCategories, c.VehicleTypes, c.Units, c.GPSProviders, c.VehicleStatuses].every((list) => list?.length > 0);

@Injectable()
export class CatalogsVehicleEffects {
  constructor(
    private actions$: Actions,
    private catalogsService: CatalogsVehicleStoreService,
    private alert: ArkikAlertService,
    private translation: TranslationService,
    private store: Store,
    private idb: ManageIdbService,
  ) {}

  loadCatalogsVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogsVehicleActions.loadCatalogsVehicle),
      mergeMap((request) =>
        defer(() => this.idb.prepareCatalogAccess(this.idb.catalogEpoch)).pipe(
          switchMap(() => this.store.select(selectCatalogsVehicleByUserGuid(request.userGuid))),
          take(1),
          switchMap((catalogs) => this.load(catalogs, request.userGuid, this.idb.catalogEpoch)),
          timeout(CATALOG_OPERATION_TIMEOUT_MS),
          map((catalogs) => CatalogsVehicleActions.loadCatalogsVehicleSuccess({ ...request, catalogs })),
          catchError((error: unknown) =>
            of(
              CatalogsVehicleActions.loadCatalogsVehicleFailure({
                ...request,
                error: reportCatalogError(
                  this.alert,
                  'vehicles',
                  request,
                  error,
                  this.translation.pt('views.globals.an-error-occurred'),
                  this.translation.pt('views.globals.reload'),
                ),
              }),
            ),
          ),
          takeUntil(merge(this.idb.catalogCancellation$, this.actions$.pipe(ofType(loadLogin, logout, resetLogin)))),
        ),
      ),
    ),
  );

  refreshCatalogsVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogsVehicleActions.refreshCatalogsVehicle),
      map(() => CatalogsVehicleActions.resetCatalogsVehicle()),
    ),
  );

  private load(
    catalogs: CatalogsVehicleInterface | null,
    userGuid: string,
    epoch: number,
  ): Observable<CatalogsVehicleInterface> {
    return defer(() => this.idb.selectVehicleCatalogsSnapshot()).pipe(
      switchMap((snapshot) => {
        if (hasCompleteVehicleCatalogsData(catalogs)) {
          return snapshot
            ? of(catalogs)
            : from(this.idb.saveVehicleCatalogs(catalogs, epoch)).pipe(map(() => catalogs));
        }
        if (snapshot) {
          return of(snapshot);
        }
        return this.catalogsService
          .getCatalogsVehicle(userGuid)
          .pipe(switchMap((response) => from(this.idb.saveVehicleCatalogs(response, epoch)).pipe(map(() => response))));
      }),
    );
  }
}
